/* eslint-disable complexity */
import { apiClientService } from "../../services/APIClientService";
import { useCallback, useEffect } from "react";
import {
	CUSTOMER_REFUSAL_MIN_MONTHS,
	ERROR_ALREADY_OPERATION,
	ERROR_AMOUNT_AVAILABLE,
	ERROR_KEY_PIX_INCORRECT,
	ERROR_KEY_PIX_UNREGISTERED,
	ERROR_PERIODS_NOT_FOUND,
} from "../../constants";
import type { AxiosError } from "axios";
import { ErrorUnavailableEnum } from "../../enum/errorUnavailable";
import { useNavigateCustom } from "../useNavigateCustom";
import { getErrorMessageByUrl } from "./errorMessageUtil";

interface IError {
	code: string;
	title: string;
	detail: string;
	message: string;
	field?: string;
}

export const useInterceptors = (): void => {
	const navigate = useNavigateCustom();

	// Navega para a tela de sistema indisponível
	const navigateSystemUnavailable = useCallback((url: string | undefined): void => {
		const { code, errorMessage } = getErrorMessageByUrl(url || "");

		if (url?.includes("/token")) {
			navigate("/system-maintenance", { replace: true, state: { code, errorMessage } });
		} else {
			navigate("/system-unavailable", { replace: true, state: { code, errorMessage } });
		}
	}, []);

	useEffect(() => {
		apiClientService.interceptors.response.use(
			(response) => response,
			async (e) => {
				// Forçando o erro ser do tipo AxiosError
				const error = e as AxiosError<{ errors: IError[] }>;
				if (error.response) {
					const url = error.response.config.url;
					const errors = error.response.data.errors;
					const method = error.response.config.method?.toUpperCase();

					if (error.response.status >= 400) {
						switch (error.response.status) {
							case 400:
								// Verificar se é um erro de caracteres especiais no endereço
								if (
									url?.includes("postal-addresses") &&
									errors.some((e) => e.message.includes("special characters") || e.message.includes("must not contain"))
								) {
									// Apenas propaga o erro sem redirecionar
									return Promise.reject(error);
								}

								if (url?.includes("contracts/signed") && errors.some((e) => e.detail === ERROR_ALREADY_OPERATION)) {
									navigate(`/error/${ErrorUnavailableEnum.CONTRACT_UNAVAILABLE}`, { replace: true });
									return Promise.reject(error);
								}

								if (url?.includes("contracts/signed") && errors.some((e) => e.message === ERROR_KEY_PIX_INCORRECT)) {
									navigate("/error-pix-key-incorrect", { replace: true });
									return Promise.reject(error);
								}

								if (url?.includes("/fgts/total-simulation") && errors.some((e) => e.code === "COP000335")) {
									return Promise.reject(error);
								}

								navigateSystemUnavailable(url);
								return await Promise.reject(error);
							case 401:
								navigate("/expired-token");
								return await Promise.reject(error);
							case 403:
								// Erro caso o cliente tenha um contrato vigente com quitação superior a 24 meses
								if (url?.includes("fgts/total-simulation")) {
									if (errors.some((e) => e.detail === ERROR_AMOUNT_AVAILABLE)) {
										navigate(`/error/${ErrorUnavailableEnum.INSUFFICIENT_BALANCE}`, { replace: true });
										return Promise.reject(error);
									}

									navigate("/error-open-settlement-with-upper-limit");
									return Promise.reject(error);
								}

								if (url?.includes("fgts/contracts/eligible")) {
									navigate("/customer-refusal", { replace: true });
									return Promise.reject(error);
								}

								if (errors.some((e) => e.detail === CUSTOMER_REFUSAL_MIN_MONTHS)) {
									navigate("/customer-refusal", { replace: true });
									return Promise.reject(error);
								}

								if (url?.includes("otp/validate")) {
									return Promise.reject(error);
								}
								if (url?.includes("password/validate")) {
									return Promise.reject(error);
								}

								return navigateSystemUnavailable(url);
							case 404:
								if (url?.includes("/customers/v1/fgts/vivo/eligible")) {
									navigate("/customer-refusal");
								}

								if (url?.includes("contracts/signed") && errors.some((e) => e.title === ERROR_KEY_PIX_UNREGISTERED)) {
									navigate("/error-pix-key-incorrect", { replace: true });
									return Promise.reject(error);
								}

								if (
									url?.includes("fgts/total-simulation") &&
									errors.some((e) => e.detail === ERROR_PERIODS_NOT_FOUND)
								) {
									navigate(`/error/${ErrorUnavailableEnum.INSUFFICIENT_BALANCE}`, { replace: true });
									return Promise.reject(error);
								}
								if (url?.includes("cep") || url?.includes("fraud-analysis")) {
									return Promise.reject(error);
								}

								if (url?.includes("fgts/balance-inquiry-request") && method === "GET") {
									return Promise.reject(error);
								}

								if (url?.includes("bff/fraud-analysis/v1")) {
									navigateSystemUnavailable(url);
									return Promise.reject(error);
								}

								navigateSystemUnavailable(url);
								return await Promise.reject(error);
							case 409:
								return Promise.reject(error);
							case 500:
								navigateSystemUnavailable(url);
								return await Promise.reject(error);
							default:
								navigateSystemUnavailable(url);
								return await Promise.reject(error);
						}
					}
				}

				return Promise.reject(error);
			},
		);
	}, []);
};
